import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LayoutGrid, List, Music, Pencil, Play, Plus, Search, Trash2, X } from "lucide-react";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { SelmaAdminMobileNav, SelmaAdminSidebar } from "@/components/admin/SelmaAdminSidebar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

// Numeroloji admin panelindeki meditasyon müziği yönetiminin birebir kopyası —
// aynı arama/sıralama/grid-liste pattern'i, tablo/endpoint izole:
// duygusifa_meditasyon_muzikleri / /api/admin/selma/meditasyon-muzikleri.
// Bkz. CLAUDE.md → "Selma Admin Paneli".

type MeditasyonMuzigi = {
  id: string;
  isim: string;
  dosya_url: string;
  kategori: string | null;
  created_at: string;
};

const EMPTY_FORM = { isim: "", dosya_url: "", kategori: "" };
type FormState = typeof EMPTY_FORM;

async function fetchItems(): Promise<MeditasyonMuzigi[]> {
  const res = await adminFetch("/api/admin/selma/meditasyon-muzikleri");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Liste alınamadı.");
  return Array.isArray(json?.data) ? json.data : [];
}

async function createItem(payload: Record<string, unknown>): Promise<void> {
  const res = await adminFetch("/api/admin/selma/meditasyon-muzikleri", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Kaydedilemedi.");
}

async function updateItem(payload: Record<string, unknown> & { id: string }): Promise<void> {
  const res = await adminFetch(`/api/admin/selma/meditasyon-muzikleri/${encodeURIComponent(payload.id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Güncellenemedi.");
}

async function deleteItem(id: string): Promise<void> {
  const res = await adminFetch(`/api/admin/selma/meditasyon-muzikleri/${encodeURIComponent(id)}`, { method: "DELETE" });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Silinemedi.");
}

async function uploadFile(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("bucket", "duygusifa-media");
  formData.append("folder", "meditasyon-muzikleri");
  const res = await adminFetch("/api/admin/selma/upload", { method: "POST", body: formData });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Yükleme başarısız.");
  return json.url as string;
}

export default function AdminSelmaMeditasyonMuzikleri() {
  const queryClient = useQueryClient();
  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editingItem, setEditingItem] = useState<MeditasyonMuzigi | null>(null);
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);
  const [audio] = useState(() => (typeof Audio !== "undefined" ? new Audio() : null));
  const [uploadLoading, setUploadLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"newest" | "title-asc">("newest");
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");

  const listQuery = useQuery({
    queryKey: ["selma-meditasyon-muzikleri"],
    queryFn: fetchItems,
    enabled: authenticated,
    staleTime: 0,
  });

  const createMutation = useMutation({
    mutationFn: createItem,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["selma-meditasyon-muzikleri"], exact: true });
      setShowForm(false);
      setForm(EMPTY_FORM);
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateItem,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["selma-meditasyon-muzikleri"], exact: true });
      setEditingItem(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteItem,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["selma-meditasyon-muzikleri"], exact: true });
    },
  });

  function handlePlay(url: string) {
    if (!audio) return;
    if (playingUrl === url) {
      audio.pause();
      audio.currentTime = 0;
      setPlayingUrl(null);
    } else {
      audio.src = url;
      audio.play();
      setPlayingUrl(url);
      audio.onended = () => setPlayingUrl(null);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (createMutation.isPending || !form.isim.trim() || !form.dosya_url.trim()) return;
    createMutation.mutate({
      isim: form.isim.trim(),
      dosya_url: form.dosya_url.trim(),
      kategori: form.kategori.trim() || null,
    });
  }

  const items = listQuery.data ?? [];

  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = q
      ? items.filter((it) => `${it.isim} ${it.kategori ?? ""}`.toLowerCase().includes(q))
      : items;
    const sorted = [...rows];
    if (sort === "title-asc") sorted.sort((a, b) => a.isim.localeCompare(b.isim, "tr"));
    return sorted;
  }, [items, search, sort]);

  if (sessionQuery.isLoading) {
    return <div className="p-8 text-sm text-slate-500">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  return (
    <div className="flex min-h-dvh w-full bg-slate-100/60">
      <SelmaAdminSidebar />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-b border-slate-200 bg-white/80 px-4 py-3 backdrop-blur sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Music className="h-4 w-4 text-slate-500" />
              <div className="text-base font-semibold text-slate-900 sm:text-lg">Selma — Meditasyon Müziği</div>
            </div>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => logoutMutation.mutate()} disabled={logoutMutation.isPending}>
              Çıkış
            </Button>
          </div>
          <div className="mt-3">
            <SelmaAdminMobileNav />
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          {listQuery.isLoading ? (
            <div className="text-sm text-slate-500">Yükleniyor...</div>
          ) : listQuery.isError ? (
            <div className="text-sm text-destructive">{(listQuery.error as Error).message}</div>
          ) : (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-2xl font-extrabold tracking-tight text-slate-900">Meditasyon Müziği</div>
                  <div className="mt-1 text-sm text-slate-500">Meditasyonların arka planında çalan doğa/enstrüman ses kütüphanesi.</div>
                </div>
                <Button size="sm" className="rounded-full" onClick={() => setShowForm(true)}>
                  <Plus className="mr-1 h-4 w-4" />
                  Yeni Ekle
                </Button>
              </div>

              {showForm && (
                <form onSubmit={handleSubmit} className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="font-semibold text-slate-900">Yeni Meditasyon Müziği</div>
                    <button type="button" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}>
                      <X className="h-4 w-4 text-slate-500" />
                    </button>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">İsim *</label>
                      <input
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                        value={form.isim}
                        onChange={(e) => setForm((f) => ({ ...f, isim: e.target.value }))}
                        placeholder="örn: Yağmur Sesi"
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Kategori</label>
                      <input
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                        value={form.kategori}
                        onChange={(e) => setForm((f) => ({ ...f, kategori: e.target.value }))}
                        placeholder="örn: doğa, enstrümantal"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Ses Dosyası (dosya_url) *</label>
                      <div className="flex items-center gap-3">
                        <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
                          {uploadLoading ? "Yükleniyor…" : "Dosya Seç"}
                          <input
                            type="file"
                            accept="audio/*"
                            className="hidden"
                            disabled={uploadLoading}
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              setUploadLoading(true);
                              try {
                                const url = await uploadFile(file);
                                setForm((f) => ({ ...f, dosya_url: url }));
                              } catch (err: any) {
                                alert(err.message ?? "Yükleme başarısız.");
                              } finally {
                                setUploadLoading(false);
                                e.target.value = "";
                              }
                            }}
                          />
                        </label>
                        {form.dosya_url && <span className="truncate text-xs text-emerald-700">✓ {form.dosya_url.split("/").pop()}</span>}
                      </div>
                      <input
                        className="mt-2 h-9 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs text-slate-700 outline-none"
                        value={form.dosya_url}
                        onChange={(e) => setForm((f) => ({ ...f, dosya_url: e.target.value }))}
                        placeholder="ya da URL yapıştır..."
                        required
                      />
                    </div>
                  </div>

                  {createMutation.isError && (
                    <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                      {(createMutation.error as Error).message}
                    </div>
                  )}

                  <div className="mt-4 flex gap-2">
                    <Button type="submit" size="sm" className="rounded-full" disabled={createMutation.isPending || uploadLoading}>
                      {createMutation.isPending ? "Kaydediliyor…" : "Kaydet"}
                    </Button>
                    <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}>
                      İptal
                    </Button>
                  </div>
                </form>
              )}

              <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    className="h-11 w-full rounded-2xl bg-slate-100 pl-10 pr-4 text-sm text-slate-900 outline-none ring-emerald-600/20 focus:ring-4"
                    placeholder="İsim, kategori..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-2">
                    <div className="text-xs font-medium text-slate-500">Sırala:</div>
                    <select
                      className="h-10 rounded-full border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none"
                      value={sort}
                      onChange={(e) => setSort(e.target.value as any)}
                    >
                      <option value="newest">En Yeni</option>
                      <option value="title-asc">İsim (A-Z)</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-1 rounded-2xl border border-slate-200 bg-white p-1">
                    <button
                      type="button"
                      className={cn("grid h-8 w-8 place-items-center rounded-xl transition-colors", viewMode === "grid" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900")}
                      onClick={() => setViewMode("grid")}
                      title="Grid görünümü"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className={cn("grid h-8 w-8 place-items-center rounded-xl transition-colors", viewMode === "list" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900")}
                      onClick={() => setViewMode("list")}
                      title="Liste görünümü"
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {deleteMutation.isError && (
                <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  {(deleteMutation.error as Error).message}
                </div>
              )}

              {editingItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      updateMutation.mutate({
                        id: editingItem.id,
                        isim: editingItem.isim,
                        dosya_url: editingItem.dosya_url,
                        kategori: editingItem.kategori,
                      });
                    }}
                    className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border border-slate-200 bg-white shadow-xl"
                  >
                    <div className="flex flex-shrink-0 items-center justify-between rounded-t-2xl border-b border-slate-200 bg-white p-6 pb-4">
                      <div className="font-semibold text-slate-900">Müziği Düzenle</div>
                      <button type="button" onClick={() => setEditingItem(null)}>
                        <X className="h-4 w-4 text-slate-500" />
                      </button>
                    </div>
                    <div className="overflow-y-auto p-6">
                      <div className="grid gap-4">
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">İsim *</label>
                          <input
                            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                            value={editingItem.isim}
                            onChange={(e) => setEditingItem((p) => p && { ...p, isim: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Kategori</label>
                          <input
                            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                            value={editingItem.kategori ?? ""}
                            onChange={(e) => setEditingItem((p) => p && { ...p, kategori: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Ses Dosyası (dosya_url) *</label>
                          <input
                            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                            value={editingItem.dosya_url}
                            onChange={(e) => setEditingItem((p) => p && { ...p, dosya_url: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      {updateMutation.isError && (
                        <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                          {(updateMutation.error as Error).message}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-shrink-0 gap-2 rounded-b-2xl border-t border-slate-200 bg-white p-6 pt-4">
                      <Button type="submit" size="sm" className="rounded-full" disabled={updateMutation.isPending}>
                        {updateMutation.isPending ? "Kaydediliyor…" : "Kaydet"}
                      </Button>
                      <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => setEditingItem(null)}>
                        İptal
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {viewMode === "grid" ? (
                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {visibleItems.map((item) => (
                    <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-bold text-slate-900">{item.isim}</div>
                        <div className="flex flex-shrink-0 items-center gap-1">
                          <button type="button" className="rounded-xl p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" onClick={() => setEditingItem(item)}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            className="rounded-xl p-1.5 text-rose-500 hover:bg-rose-50"
                            disabled={deleteMutation.isPending}
                            onClick={() => {
                              if (confirm(`"${item.isim}" silinsin mi?`)) deleteMutation.mutate(item.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {item.kategori && <div className="mt-1 text-xs text-slate-400">{item.kategori}</div>}
                      <button type="button" onClick={() => handlePlay(item.dosya_url)} className="mt-3 flex items-center gap-2 rounded-xl bg-emerald-600/10 px-3 py-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-600/20">
                        <Play className="h-3.5 w-3.5" />
                        {playingUrl === item.dosya_url ? "Durdur" : "Oynat"}
                      </button>
                    </div>
                  ))}
                  {items.length === 0 && !showForm && (
                    <button type="button" onClick={() => setShowForm(true)} className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-white p-8 text-slate-500 hover:border-emerald-400/50 hover:bg-emerald-50/50">
                      <Plus className="mb-2 h-8 w-8" />
                      <div className="font-bold">İlk müziği ekle</div>
                    </button>
                  )}
                </div>
              ) : (
                <div className="mt-6 flex flex-col gap-2">
                  {visibleItems.map((item) => (
                    <div key={item.id} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-bold text-slate-900">{item.isim}</div>
                        {item.kategori && <div className="text-xs text-slate-400">{item.kategori}</div>}
                      </div>
                      <div className="flex flex-none items-center gap-1">
                        <button type="button" onClick={() => handlePlay(item.dosya_url)} className="grid h-8 w-8 place-items-center rounded-xl bg-emerald-600/10 text-emerald-700 hover:bg-emerald-600/20">
                          <Play className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200" onClick={() => setEditingItem(item)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-rose-600 hover:bg-rose-50"
                          disabled={deleteMutation.isPending}
                          onClick={() => {
                            if (confirm(`"${item.isim}" silinsin mi?`)) deleteMutation.mutate(item.id);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && !showForm && (
                    <button type="button" onClick={() => setShowForm(true)} className="flex items-center gap-4 rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 py-4 text-slate-600 transition-colors hover:border-emerald-500/50 hover:bg-emerald-50/50">
                      <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-slate-100 text-slate-900">
                        <span className="text-xl">+</span>
                      </div>
                      <div className="text-sm font-bold">İlk müziği ekle</div>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
